import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

const REFERENCE_CASES = [
  {
    to: "/works/lvf-tv",
    chip: "Business Impact · Subscriber Acquisition",
    title: "LVF TV",
    kpi: "10.000+ abbonati raggiunti · ROI Black Friday +1.926%",
    image:
      "/images/assets/7la9gmlo_2.jpg",
  },
  {
    to: "/works/protek",
    chip: "Business Impact · Brand Owner DTC",
    title: "Protek",
    kpi: "1.500+ paia vendute nei primi 6 mesi · ROAS +262%",
    image:
      "/images/assets/l4g2rl7a_lg1a8605-scaled-1.jpg",
  },
  {
    to: "/works/empower",
    chip: "Business Impact · Brand Creation & E-commerce",
    title: "Empower (Biolife)",
    kpi: "1.000+ kit venduti nel primo mese · ROAS 236%",
    image:
      "/images/assets/r96ciop1_main-photo.jpg",
  },
];

const METHOD_STEPS = [
  {
    num: "01",
    title: "Strategy & concept",
    body:
      "Definizione del posizionamento di lancio, identificazione del segmento target, costruzione del messaggio chiave. È la fase in cui decidiamo cosa il consumatore deve sentire al primo touchpoint.",
  },
  {
    num: "02",
    title: "Sales page & UX",
    body:
      "Sviluppo della sales page di conversione: architettura del messaggio, copy commerciale, integrazione checkout, ottimizzazione mobile-first. È il punto di arrivo del funnel — l'errore qui costa più che in qualsiasi altra fase.",
  },
  {
    num: "03",
    title: "Paid media & email",
    body:
      "Setup e gestione delle campagne Google Ads e Meta Ads. Costruzione dei flussi email automation. Ottimizzazione continuativa sulle metriche di conversione (CPA, CR, AOV).",
  },
  {
    num: "04",
    title: "Reporting & scaling",
    body:
      "Reporting settimanale durante la campagna, dashboard real-time sui KPI condivisi, decisioni di scaling basate sui dati. A fine campagna, debrief con il cliente e piano per il ciclo successivo.",
  },
];

const DELIVERABLES = [
  { output: "Strategy & concept di lancio", when: "Pre-lancio", note: "Documento approvato dal cliente" },
  { output: "Sales page sviluppata", when: "Pre-lancio", note: "Mobile-first, integrazione checkout" },
  { output: "Setup tracking & analytics", when: "Pre-lancio", note: "Pixel, GA4, GTM, attribuzione" },
  { output: "Campagne Google Ads", when: "Lancio + ongoing", note: "Search + Performance Max" },
  { output: "Campagne Meta Ads", when: "Lancio + ongoing", note: "Prospecting + retargeting" },
  { output: "Email automation", when: "Lancio", note: "Welcome, abandoned cart, post-purchase" },
  { output: "Reporting settimanale", when: "Ongoing", note: "Dashboard + sintesi" },
  { output: "Ottimizzazione continuativa", when: "Ongoing", note: "A/B test creative, audience, copy" },
];

const PROFILES = [
  {
    title: "Brand DTC sport-consumer",
    body:
      "Brand che vendono direttamente al consumatore: sportswear, accessori, integratori, dispositivi. Sia in lancio sia in fase di scaling.",
  },
  {
    title: "Piattaforme streaming & abbonamento",
    body:
      "Servizi che vendono accesso ricorrente a contenuti sportivi (streaming, fitness, training, fantasy). Subscriber acquisition + retention.",
  },
  {
    title: "E-commerce sport in fase di lancio",
    body:
      "Brand consumer che entrano nello sport per la prima volta e vogliono costruire il proprio canale e-commerce con regia integrata.",
  },
];

const KPIS = [
  { kpi: "ROAS (Return on Ad Spend)", misura: "Ricavi attribuibili / spesa paid" },
  { kpi: "CPA (Cost per Acquisition)", misura: "Spesa paid / numero clienti acquisiti" },
  { kpi: "Conversion Rate", misura: "Acquisti / visitatori della sales page" },
  { kpi: "AOV (Average Order Value)", misura: "Ricavi totali / numero ordini" },
  { kpi: "LTV / CAC", misura: "Lifetime value / costo acquisizione cliente" },
  { kpi: "Funnel completion rate", misura: "% che completa il funnel dal primo touchpoint all'acquisto" },
];

const FAQS = [
  {
    q: "Quando vedo i primi risultati?",
    a:
      "ROAS misurabile dalla prima settimana di paid attiva. KPI strutturali (CPA, CR, AOV) entro le prime 3-4 settimane. Scaling decisions dopo il primo mese di dati.",
  },
  {
    q: "Lavorate solo su Meta e Google o anche su TikTok, LinkedIn?",
    a:
      "Lavoriamo prevalentemente Meta + Google perché sono i canali in cui il rapporto qualità/scalabilità è più alto per la maggior parte dei prodotti sport-consumer italiani. Su TikTok, LinkedIn e Pinterest interveniamo solo se il prodotto e il target lo giustificano davvero.",
  },
  {
    q: "Cosa succede se il ROAS non torna?",
    a:
      "Lavoriamo a obiettivi condivisi, non a budget bruciato. Se i numeri non tornano, fermiamo, capiamo perché, ricalibriamo. Il framework è progettato per essere sostenibile, non per nascondere perdite.",
  },
  {
    q: "Sostituite la nostra agenzia digital attuale?",
    a:
      "Spesso no. Ci affianchiamo come consulenti del coordinamento e operativi sui blocchi non coperti. La regia unica è il valore — non la sostituzione dei fornitori esistenti.",
  },
  {
    q: "Come si configura un progetto Performance Marketing & Growth?",
    a:
      "Ogni progetto è progettato sulla base del mercato del prodotto, del target di acquisizione e dei KPI condivisi con il cliente. La configurazione esatta — durata, perimetro operativo, canali attivi, struttura del team coinvolto — la definiamo insieme durante la fase di scoping iniziale, dopo aver letto il prodotto e ascoltato il cliente.",
  },
];

const ServizioPerformanceMarketing = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "Performance Marketing & Growth", url: "/servizi/performance-marketing-growth" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "Performance Marketing & Growth",
    description: "Performance marketing per brand sportivi: Meta Ads, Google Ads, conversion optimization.",
    slug: "performance-marketing-growth",
  });

  return (
    <>
      <Seo
        title="Performance Marketing: ROAS e lead brand sportivi | OnSports"
        description="Performance marketing per brand sportivi e property: Meta Ads, Google Ads, email marketing, ottimizzazione conversion. ROAS misurato, lead in target."
        path="/servizi/performance-marketing-growth"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-performance-marketing"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="06" className="mt-8">
              Performance Marketing &amp; Growth
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Acquisition · Sales page · Paid media · ROAS
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">Dallo sport</span>
              <span className="block">alle</span>
              <span className="block text-lime">CONVERSIONI.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Acquisizione utenti, sales page, paid media e email marketing
              per piattaforme, e-commerce e prodotti sport-consumer. ROAS
              tracciabile dal primo mese, KPI condivisi prima del lancio,
              esecuzione end-to-end sotto un'unica regia.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — Brand DTC, piattaforme streaming, e-commerce sport, prodotti consumer</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Funnel completo, sales page, paid media, reporting</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Acquisizione e brand vivono ancora in mondi separati.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Quando un brand decide di lanciare un prodotto
                  sport-consumer (una scarpa, una piattaforma streaming,
                  un bundle, un abbonamento) le filiere coinvolte sono
                  almeno quattro: creative agency per il concept, talent
                  agency per i testimonial, web agency per la sales page,
                  media agency per il paid. Ognuna lavora con i propri
                  KPI, i propri tempi, i propri canali. Tra una filiera e
                  l'altra si perdono dati, messaggio, e settimane di
                  lavoro.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il problema diventa visibile in fase di go-to-market. La
                  creative parte da una storia. La sales page la traduce
                  in copy e visual che spesso sono diversi. La campagna
                  Meta lavora su altri creative ancora — perché il media
                  buyer non ha visto la creative originale ed è obbligato
                  a ottimizzare per CPA. L'email marketing parla un tono
                  ancora diverso. Il risultato è un funnel frammentato, in
                  cui il consumatore vede tre o quattro versioni dello
                  stesso brand nelle 48 ore di decisione d'acquisto.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  L'alternativa è semplice da descrivere e difficile da
                  eseguire: tenere tutte le filiere sotto un'unica regia,
                  dal concept al ROAS. È quello che facciamo per i clienti
                  del servizio Performance Marketing &amp; Growth.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FACCIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-cosa-facciamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Cosa facciamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Il funnel completo. Sotto un'unica regia.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Lavoriamo con brand sport-consumer (sportswear,
                  accessori, integratori, dispositivi), piattaforme
                  streaming e abbonamento, e-commerce sport, brand DTC che
                  entrano nel mercato sport per la prima volta. La nostra
                  promessa operativa è precisa: ROAS tracciabile dal primo
                  mese, KPI condivisi col cliente prima del lancio,
                  scorecard settimanale durante la campagna.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il servizio copre l'intera filiera: concept e creative
                  coordination, sviluppo della sales page di conversione,
                  setup del tracking analytics, gestione delle campagne
                  paid su Google Ads e Meta Ads (prospecting +
                  retargeting), email marketing automation (welcome,
                  abandoned cart, post-purchase), reporting trimestrale.
                  Quando il cliente già ha alcuni di questi elementi,
                  lavoriamo sui buchi della catena e otteniamo lo stesso
                  effetto di coordinamento.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Non sostituiamo l'agenzia digital interna del cliente,
                  se esiste. Ci affianchiamo come consulenti del
                  coordinamento e operativi sui blocchi non coperti. La
                  regia unica è il valore — non l'esclusività sul lavoro.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL METODO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-metodo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Il metodo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quattro filiere. Stessa lingua.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {METHOD_STEPS.map((step, i) => (
              <RevealOnScroll key={step.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-dodgers/[0.02] h-full flex flex-col"
                  data-testid={`method-step-${step.num}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-lime text-3xl md:text-4xl leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {step.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 5 — COSA INCLUDIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-includiamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa includiamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Cosa ricevi.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="deliverables-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Output</th>
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Quando</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map((d, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top">{d.output}</td>
                      <td className="py-4 pr-6 align-top opacity-80">{d.when}</td>
                      <td className="py-4 align-top opacity-80">{d.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — PER CHI È */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre tipologie tipiche.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CASI DI RIFERIMENTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-casi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Casi di riferimento</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Conversioni reali.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {REFERENCE_CASES.map((c, i) => (
              <RevealOnScroll key={c.to} delay={i * 0.06}>
                <Link
                  to={c.to}
                  className="group block focus:outline-none"
                  data-hover="true"
                  data-testid={`case-card-${i}`}
                >
                  <div className="overflow-hidden bg-galactico/[0.05]" style={{ aspectRatio: "4 / 3" }}>
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                      draggable="false"
                    />
                  </div>
                  <span className="block mt-4 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    ● {c.chip}
                  </span>
                  <h3 className="mt-2 font-display text-xl md:text-2xl leading-tight group-hover:text-lime transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.55] opacity-85">{c.kpi}</p>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 8 — MISURAZIONE & KPI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-kpi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Misurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">KPI condivisi prima del lancio.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="kpi-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal w-[36%]">KPI</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Misura</th>
                  </tr>
                </thead>
                <tbody>
                  {KPIS.map((k, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top font-display text-base md:text-lg">{k.kpi}</td>
                      <td className="py-4 align-top opacity-90">{k.misura}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — FAQ */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-galactico/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Misuriamo insieme il tuo <span className="text-lime">ROAS</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              Una call di 30 minuti per capire se siamo gli interlocutori giusti.
              KPI condivisi prima di qualunque conversazione operativa.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta consulenza — Performance Marketing & Growth"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioPerformanceMarketing;
